import { useEffect, useCallback, useState } from "react";
import ProductCard from "@/components/Helpers/ProductCard";
import Side from "@/components/Search/Side";
import ResponsiveFilter from "../Search/ResponsiveFilter";
import Pagination from "../new-category/pagination";
import { useRouter } from "next/router";
import EmptySearchContent from "./EmptySearchContent";
import LoadingSearch from "./LoadingSearch";
import { useInstantSearch } from "react-instantsearch-hooks";
import Cookies from "js-cookie";
import SortingAndSearchCount from "./SortingAndSearchCount";
import ResponsiveSort from "../Search/ResponsiveSort";
import ResponsiveFilterButtons from "./ResponsiveFilterButtons";
import { trackClickedFilters } from "@/utils/searchClients";
import { checkSearchLang } from "@/utils/global";
import Banners from "../new-category/head/banners";
import useAlgoliaFacets from "@/hooks/algolia/useAlgoliaFacets";

const SearchPageContent = ({
  t,
  page,
  setPage,
  device,
  products,
  suggestions,
  numberOfPages,
  setSelectedFilterKeys,
  selectedFilterKeys,
  searchQuery,
  numberOfHits,
  setCurrentRefinement,
  currentRefinement,
  queryId,
  banners,
  facets,
}) => {
  const { locale, push } = useRouter();
  const { status } = useInstantSearch();
  const isSearching = status === "loading";
  const [loading, setLoading] = useState(true);
  const regionId = +(Cookies.get("region_id") || "1101");

  useEffect(() => {
    isSearching
      ? setTimeout(() => setLoading(true), 100)
      : setTimeout(() => setLoading(false), 1000);
  }, [isSearching]);

  const [openFilterBox, setOpenFilterBox] = useState(false);
  const [openSortBox, setOpenSortBox] = useState(false);

  const updatedFacets = useAlgoliaFacets(facets);

  const indexName =
    checkSearchLang(searchQuery, locale) === "ar"
      ? currentRefinement
      : currentRefinement?.replace("ar", "en");

  const handleFacetRefinement = useCallback(
    (obj, itemParent) => {
      const filterKey = `${itemParent.attribute}:${obj.code}`;

      setSelectedFilterKeys((prev) =>
        prev.includes(filterKey)
          ? prev.filter((key) => key !== filterKey)
          : [...prev, filterKey]
      );

      setPage(1);
      trackClickedFilters(indexName, [filterKey]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSelectedFilterKeys, setPage]
  );

  const onSearch = (value) => {
    if (!value.trim()) return;
    push(`/search?q=${encodeURIComponent(value)}`);
    setPage(1);
  };

  const handlePageChange = useCallback(
    (newPage) => {
      if (newPage < 1 || newPage > numberOfPages) return;
      setPage(newPage);
    },
    [setPage, numberOfPages]
  );

  return (
    <>
      <section className="w-11/12 max-[991px]:w-[98%] my-2 mx-auto Search-body bg-white">
        {!!banners?.length && (
          <div className="mb-4">
            <Banners banners={banners} name={banners[0]?.name} />
          </div>
        )}

        {products?.length ? (
          <div className="grid grid-cols-12 gap-4 relative">
            <Side
              {...{
                // searchCategories,
                filterBy: updatedFacets,
                onSearch,
                suggestWords: suggestions || "",
                onFilter: handleFacetRefinement,
                selectedFilterKeys,
              }}
            />
            <div
              className={`col-span-9 ${
                !banners?.length && "mt-2"
              } bg-white rounded-md max-[768px]:col-span-12`}
            >
              <SortingAndSearchCount
                t={t}
                totalProduct={numberOfHits}
                page={page}
                setCurrentRefinement={setCurrentRefinement}
                currentRefinement={currentRefinement}
              />

              <ResponsiveFilterButtons
                {...{
                  t,
                  setOpenFilterBox,
                  setOpenSortBox,
                }}
              />
              <div className="grid grid-cols-4 gap-3 mb-4 max-[991px]:grid-cols-3 max-[768px]:grid-cols-2 2sm:grid-cols-2">
                {products?.map((product, index) => (
                  <ProductCard
                    fromProduct={true}
                    isShowCompareBtn={false}
                    key={product?.sku}
                    // to send the exact position of hit with pagination (32 is hits per page)
                    position={index + 1 + (page - 1) * 32}
                    queryId={queryId}
                    indexName={indexName}
                    {...{
                      t,
                      highlight: {
                        "name.standard": product?._highlightResult?.name?.value
                          ?.replace(/<(em|mark)>/g, "<strong>")
                          ?.replace(/<\/(em|mark)>/g, "</strong>"),
                      },
                      product: {
                        ...product,
                        [regionId]: {
                          value: product?.stock_region_ids[regionId],
                        },
                        option_text_brand: product?.brand,
                        image: [
                          product?.image_url?.replace(
                            /^.*\/catalog\/product/,
                            ""
                          ),
                        ],
                        _media_: { image: [{ image: product?.image_url }] },
                      },
                    }}
                  />
                ))}
              </div>

              <Pagination
                currentPage={page}
                pageCount={numberOfPages}
                onPageChange={handlePageChange}
                t={t}
                device={device}
              />
            </div>
          </div>
        ) : loading ? (
          <LoadingSearch locale={locale} />
        ) : (
          <EmptySearchContent locale={locale} push={push} />
        )}
      </section>

      {openFilterBox && (
        <ResponsiveFilter
          {...{
            setOpenFilterBox,
            filterBy: updatedFacets,
            selectedFilterKeys,
            onFilter: handleFacetRefinement,
            t,
          }}
        />
      )}

      {openSortBox && (
        <ResponsiveSort
          {...{
            t,
            setCurrentRefinement,
            currentRefinement,
            setOpenSortBox,
          }}
        />
      )}
    </>
  );
};

export default SearchPageContent;
